import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ApiAdapterError from "@/utils/ApiAdapterError";
import {
  cancelWorkspaceBilling,
  convertWorkspaceBilling,
  getWorkspaceUpdatePaymentMethodTransaction,
  previewWorkspaceBillingChange,
  resumeWorkspaceBilling,
  updateWorkspaceBilling,
  updateWorkspaceBillingEmail,
  type WorkspaceBillingChangePreviewOutput,
  type WorkspaceUpdatePaymentMethodOutput,
} from "../api/workspaceBilling";

// All billing mutations invalidate the workspace detail query: the webhook has
// already reflected the change onto the workspace record by the time the
// endpoint returns (the server polls for it).
const useInvalidateWorkspace = () => {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["workspace"] });
  };
};

export const useUpdateWorkspaceBilling = () => {
  const invalidate = useInvalidateWorkspace();

  return useMutation<
    { deferred?: boolean; nextBillDate?: string | null } | void,
    ApiAdapterError,
    { workspaceSlug: string; prices: Array<{ priceId: string; quantity: number }> }
  >({
    mutationFn: (input) => updateWorkspaceBilling(input),
    onSuccess: invalidate,
  });
};

export const useCancelWorkspaceBilling = () => {
  const invalidate = useInvalidateWorkspace();

  return useMutation<void, ApiAdapterError, { workspaceSlug: string }>({
    mutationFn: ({ workspaceSlug }) => cancelWorkspaceBilling(workspaceSlug),
    onSuccess: invalidate,
  });
};

export const useResumeWorkspaceBilling = () => {
  const invalidate = useInvalidateWorkspace();

  return useMutation<void, ApiAdapterError, { workspaceSlug: string }>({
    mutationFn: ({ workspaceSlug }) => resumeWorkspaceBilling(workspaceSlug),
    onSuccess: invalidate,
  });
};

export const useConvertWorkspaceBilling = () => {
  const invalidate = useInvalidateWorkspace();
  const queryClient = useQueryClient();

  return useMutation<void, ApiAdapterError, { workspaceSlug: string; feedIds: string[] }>({
    mutationFn: (input) => convertWorkspaceBilling(input),
    onSuccess: async () => {
      await invalidate();
      // Conversion re-homes the chosen feeds onto the workspace; the feed
      // list is cached per scope, so the workspace feeds page would otherwise
      // keep showing its stale (empty) cache. Invalidate every user-feeds
      // query (personal loses the feeds, workspace gains them).
      await queryClient.invalidateQueries({ queryKey: ["user-feeds"] });
    },
  });
};

export const useUpdateWorkspaceBillingEmail = () => {
  const invalidate = useInvalidateWorkspace();

  return useMutation<
    { data: { billingEmail: string } },
    ApiAdapterError,
    { workspaceSlug: string; email: string }
  >({
    mutationFn: (input) => updateWorkspaceBillingEmail(input),
    onSuccess: invalidate,
  });
};

// Lazily fetched on the owner's click: minting a Paddle transaction has a
// side effect, so it must not run on render. The caller invokes refetch(),
// then opens the Paddle overlay with the returned transaction id.
export const useWorkspaceUpdatePaymentMethodTransaction = (workspaceSlug: string) => {
  const { error, fetchStatus, refetch } = useQuery<
    WorkspaceUpdatePaymentMethodOutput,
    ApiAdapterError
  >({
    queryKey: ["workspace-update-payment-method-transaction", workspaceSlug],
    queryFn: async () => getWorkspaceUpdatePaymentMethodTransaction(workspaceSlug),
    gcTime: 0,
    enabled: false,
  });

  return { error, fetchStatus, refetch };
};

export const useWorkspaceBillingChangePreview = ({
  workspaceSlug,
  prices,
  enabled,
}: {
  workspaceSlug?: string;
  prices?: Array<{ priceId: string; quantity: number }>;
  enabled?: boolean;
}) => {
  const { data, status, error } = useQuery<
    WorkspaceBillingChangePreviewOutput,
    ApiAdapterError | Error
  >({
    queryKey: ["workspace-billing-preview", { workspaceSlug, prices }],
    queryFn: async () => {
      if (!workspaceSlug || !prices?.length) {
        throw new Error("Missing workspace billing preview input");
      }

      return previewWorkspaceBillingChange({ workspaceSlug, prices });
    },
    enabled: !!enabled && !!workspaceSlug && !!prices?.length,
  });

  return {
    preview: data?.data,
    status,
    error,
  };
};
